import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

async function loadSimBySlug(supabase: any, slug: string) {
  const { data, error } = await supabase.from("lxp_simulations").select("*").eq("slug", slug).eq("published", true).maybeSingle();
  if (error) throw new Error(error.message);
  return data;
}

export const startOrGetRun = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) => z.object({ slug: z.string().min(1).max(200) }).parse(i))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const sim = await loadSimBySlug(supabase, data.slug);
    if (!sim) throw new Error("Simulation not found");

    let { data: run } = await supabase
      .from("lxp_simulation_runs")
      .select("*")
      .eq("user_id", userId)
      .eq("simulation_id", sim.id)
      .maybeSingle();
    if (!run) {
      const { data: created, error } = await supabase
        .from("lxp_simulation_runs")
        .insert({ user_id: userId, simulation_id: sim.id })
        .select("*")
        .single();
      if (error) throw new Error(error.message);
      run = created;
    }
    const { data: submissions } = await supabase
      .from("lxp_task_submissions")
      .select("*")
      .eq("run_id", run.id)
      .order("task_index", { ascending: true });
    return { simulation: sim, run, submissions: submissions ?? [] };
  });

export const saveDraft = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) =>
    z.object({
      runId: z.string().uuid(),
      taskIndex: z.number().int().min(0).max(50),
      body: z.string().max(50000),
    }).parse(i),
  )
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { error } = await supabase
      .from("lxp_task_submissions")
      .upsert(
        { run_id: data.runId, user_id: userId, task_index: data.taskIndex, body_html: data.body },
        { onConflict: "run_id,task_index" },
      );
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const setPortfolioVisibility = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) => z.object({ submissionId: z.string().uuid(), visible: z.boolean() }).parse(i))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { error } = await supabase
      .from("lxp_task_submissions")
      .update({ portfolio_visible: data.visible })
      .eq("id", data.submissionId)
      .eq("user_id", userId);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const advanceTask = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) => z.object({ runId: z.string().uuid(), nextIndex: z.number().int().min(0).max(50) }).parse(i))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { error } = await supabase
      .from("lxp_simulation_runs")
      .update({ current_task_index: data.nextIndex })
      .eq("id", data.runId)
      .eq("user_id", userId);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const listMyRuns = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    const { data, error } = await supabase
      .from("lxp_simulation_runs")
      .select("id,status,current_task_index,xp_earned,final_score,started_at,completed_at,simulation:lxp_simulations(slug,title,practice_area,difficulty)")
      .eq("user_id", userId)
      .order("updated_at", { ascending: false });
    if (error) throw new Error(error.message);
    return { runs: data ?? [] };
  });
